
import React, { useState } from 'react';
import { ArrowRight, Tablet, Monitor, Laptop, Cpu, HardDrive, Wifi, Battery, Clock, Shield, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

// Product type for our data structure
type Product = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  image: string;
  altText: string;
  specs: {
    icon: React.ReactNode;
    label: string;
    value: string;
  }[];
};

const ProductsSection = () => {
  const [selectedProduct, setSelectedProduct] = useState<string>('tablets');
  
  // Product data
  const products: Product[] = [
    {
      id: 'tablets',
      name: 'Custom Tablets',
      description: 'Ruggedized, lightweight tablets designed for portability and enterprise use. Perfect for field teams and industrial environments.',
      icon: <Tablet className="h-6 w-6" />,
      features: [
        'Water and dust resistant designs',
        'Extended battery life options',
        'Industrial-grade components',
        'Custom mounting solutions',
        'Enterprise security features'
      ],
      image: '/lovable-uploads/db06e8f0-c84f-4067-95e8-1637cc01e1fc.png',
      altText: 'Professional Sony Tablet - Enterprise-grade with sleek design for business applications',
      specs: [
        { icon: <Cpu />, label: 'Processor', value: 'Up to Intel Core i7' },
        { icon: <HardDrive />, label: 'RAM', value: 'Up to 16GB' },
        { icon: <HardDrive />, label: 'Storage', value: 'Up to 1TB SSD' },
        { icon: <Wifi />, label: 'Connectivity', value: '5G, WiFi 6, Bluetooth 5.2' },
        { icon: <Battery />, label: 'Battery', value: 'Up to 14 hours' }
      ]
    },
    {
      id: 'pcs',
      name: 'Custom PCs',
      description: 'High-performance workstations built for designers, developers, and gamers. Maximize productivity with hardware tailored to your workflow.',
      icon: <Monitor className="h-6 w-6" />,
      features: [
        'Custom cooling solutions',
        'Premium component selection',
        'Noise-optimized configurations',
        'Expandable design',
        'Pre-installed software suites'
      ],
      image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&auto=format&fit=crop',
      altText: 'High-Performance Custom PC Workstation by Truecore - Premium Components for Business and Gaming',
      specs: [
        { icon: <Cpu />, label: 'Processor', value: 'Up to Intel Core i9 or AMD Ryzen 9' },
        { icon: <HardDrive />, label: 'RAM', value: 'Up to 128GB' },
        { icon: <HardDrive />, label: 'Storage', value: 'Up to 8TB combined storage' },
        { icon: <Shield />, label: 'Warranty', value: '5-year comprehensive' },
        { icon: <Clock />, label: 'Build Time', value: '5-7 business days' }
      ]
    },
    {
      id: 'desktops',
      name: 'Custom Desktops',
      description: 'Powerful desktop workstations designed for heavy computation, AI/ML workloads, and enterprise environments requiring reliability.',
      icon: <Laptop className="h-6 w-6" />,
      features: [
        'Enterprise-grade components',
        'Scalable configurations',
        'Redundant power options',
        'Server-grade reliability',
        'Advanced thermal management'
      ],
      image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&auto=format&fit=crop',
      altText: 'Enterprise-Grade Custom Desktop by Truecore - Reliable Computing for Business and AI/ML Workloads',
      specs: [
        { icon: <Cpu />, label: 'Processor', value: 'Up to dual Xeon or EPYC' },
        { icon: <HardDrive />, label: 'RAM', value: 'Up to 256GB ECC' },
        { icon: <HardDrive />, label: 'Storage', value: 'Up to 100TB enterprise storage' },
        { icon: <Shield />, label: 'Warranty', value: '3-year on-site service' },
        { icon: <Clock />, label: 'Build Time', value: '7-14 business days' }
      ]
    }
  ];
  
  // Get the currently selected product
  const currentProduct = products.find(p => p.id === selectedProduct) || products[0];
  
  return (
    <section id="products" className="section-padding bg-gradient-to-b from-white to-muted/30 relative overflow-hidden">
      {/* Background pattern for more authentic tech feel */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,_#8b5cf610_1px,_transparent_1px),_linear-gradient(to_bottom,_#8b5cf610_1px,_transparent_1px)] bg-[size:14px_24px]"></div>
      
      <div className="container mx-auto container-padding relative z-10">
        <div className="text-center mb-16">
          <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">Since 2000</span>
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            <span className="gradient-text">Custom Technology Solutions</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto mb-6"></div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Hardware built to your exact specifications, combining cutting-edge components with 
            expert engineering to create technology that performs exactly as you need it to.
          </p>
        </div>
        
        {/* Product Navigation - Redesigned */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {products.map(product => (
            <button
              key={product.id}
              onClick={() => setSelectedProduct(product.id)}
              className={`flex items-center space-x-3 px-7 py-4 rounded-full transition-all duration-300 ${
                selectedProduct === product.id 
                  ? 'bg-gradient-to-r from-primary to-primary/80 text-white shadow-md shadow-primary/20' 
                  : 'bg-white hover:bg-muted/20 text-foreground border border-border/50 hover:border-primary/30'
              }`}
            >
              <span className="bg-white/20 p-1.5 rounded-full">{product.icon}</span>
              <span className="font-medium">{product.name}</span>
            </button>
          ))}
        </div>
        
        {/* Product Details - Enhanced Design */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-border/30 transition-all duration-500 hover:shadow-primary/5">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Product Image - With overlays and design enhancements */}
            <div className="relative h-full min-h-[450px] overflow-hidden group">
              <img 
                src={currentProduct.image} 
                alt={currentProduct.altText} 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-tech-dark/90 via-tech-dark/50 to-transparent">
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <span className="inline-block px-3 py-1 bg-primary/80 text-white text-xs uppercase tracking-wider rounded-full mb-3">
                    Premium Quality
                  </span>
                  <h3 className="text-3xl font-bold mb-3">{currentProduct.name}</h3>
                  <p className="text-gray-200 text-lg mb-4 leading-relaxed max-w-md">{currentProduct.description}</p>
                  <div className="hidden md:flex items-center space-x-6 text-sm text-gray-300 mt-6">
                    <span className="flex items-center">
                      <Shield className="h-4 w-4 mr-1 text-primary" />
                      5-Year Warranty
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1 text-primary" />
                      Fast Delivery
                    </span>
                    <span className="flex items-center">
                      <Check className="h-4 w-4 mr-1 text-primary" />
                      Expert Support
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Product Info - Redesigned for better usability */}
            <div className="p-8 lg:p-10">
              <div className="border-b border-border/50 pb-6 mb-6">
                <h4 className="text-xl font-bold mb-4 flex items-center">
                  <span className="bg-primary/10 text-primary p-1 rounded mr-2">
                    <Check className="h-5 w-5" />
                  </span>
                  Key Features
                </h4>
                <ul className="space-y-3">
                  {currentProduct.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-4 flex items-center">
                  <span className="bg-secondary/10 text-secondary p-1 rounded mr-2">
                    <Cpu className="h-5 w-5" />
                  </span>
                  Technical Specifications
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentProduct.specs.map((spec, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-muted/20 rounded-lg border border-border/30 hover:border-primary/30 transition-colors">
                      <div className="text-primary bg-primary/10 p-2 rounded-full">{spec.icon}</div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{spec.label}</p>
                        <p className="font-semibold">{spec.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-center mt-8">
                <Link 
                  to={`/product/${currentProduct.id}`}
                  className="tech-btn-primary flex items-center px-8 py-3 rounded-full bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
                >
                  View Complete Details
                  <ArrowRight className="ml-2 h-5 w-5 animate-pulse" />
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Authentic Trust Indicators */}
        <div className="mt-16 flex flex-wrap justify-center gap-6 md:gap-12">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <span>5-Year Warranty</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="h-5 w-5 text-primary" />
            </div>
            <span>ISO 9001 Certified</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <span>25+ Years Experience</span>
          </div>
        </div>
        
        {/* Product Builder CTA - Redesigned */}
        <div className="mt-16 text-center bg-gradient-to-r from-primary/5 to-secondary/5 p-10 rounded-2xl border border-border/30">
          <h3 className="text-2xl font-bold mb-4">Ready to Build Your Custom Solution?</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Our product configurator helps you design the perfect hardware for your needs.
            Start with a base model and customize every component to match your requirements.
          </p>
          <a 
            href="#contact" 
            className="tech-btn-outline inline-flex items-center px-8 py-3 border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 rounded-full font-medium"
          >
            Launch Product Builder
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
